import { ReactNode } from 'react';
import { ColumnsType } from 'antd/lib/table';
import {
  projectCategoryRender,
  providenceAgentRender,
  readOnlyDatePickerRenderer,
  readOnlyInputNumberRender,
} from '@core/utils/methods';
import {
  AdditionalFilter,
  SiteAuditBrowseFilter,
  SiteAuditInformation,
  SiteBrowse,
} from '@models/interfaces';
import { Browse } from '@models/types';

export const SITE_AUDIT_COLUMNS: ColumnsType<Browse> = [
  {
    title: 'Alternate Site Info',
    width: 170,
    key: 'alternateSiteInfo',
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.alternateSiteInfo,
  },
  {
    title: 'City',
    key: 'city',
    dataIndex: 'site',
    width: 160,
    sorter: true,
    render: (site: SiteBrowse): ReactNode => site?.city,
  },
  {
    title: 'Legacy Site ID',
    width: 150,
    key: 'legacySiteId',
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.legacySiteId,
  },
  {
    title: 'Oracle Project',
    key: 'oracleProjectNumber',
    dataIndex: 'categories',
    render: projectCategoryRender({ field: 'oracleProjectNumber' }),
    width: 150,
    sorter: true,
  },
  {
    title: 'PACE Project',
    key: 'PACEProjectNumber',
    dataIndex: 'categories',
    render: projectCategoryRender({ field: 'PACEProjectNumber' }),
    width: 150,
    sorter: true,
  },
  {
    title: 'Project Category',
    dataIndex: 'categories',
    key: 'projectCategory',
    render: projectCategoryRender({ field: 'projectCategory' }),
    width: 200,
    sorter: true,
  },
  {
    title: 'Providence Agent',
    key: 'providenceAgent',
    width: 180,
    sorter: true,
    dataIndex: 'providenceAgent',
    render: providenceAgentRender,
  },
  {
    title: 'Scheduled Site Walk',
    key: 'scheduledSiteWalk',
    dataIndex: 'siteAudit',
    width: 190,
    sorter: true,
    render: (siteAudit: SiteAuditInformation): ReactNode =>
      readOnlyDatePickerRenderer(siteAudit?.scheduledSiteWalk || undefined),
  },
  {
    title: 'Site Number',
    dataIndex: 'site',
    key: 'siteNumber',
    width: 135,
    sorter: true,
    render: (site: SiteBrowse): ReactNode => site?.siteNumber,
  },
  {
    title: 'State',
    dataIndex: 'site',
    width: 110,
    key: 'state',
    sorter: true,
    render: (site: SiteBrowse): ReactNode => site?.state?.name,
  },
  {
    title: 'Street Address',
    key: 'streetAddress',
    dataIndex: 'site',
    width: 170,
    sorter: true,
    render: (site: SiteBrowse): ReactNode => site?.streetAddress,
  },
  {
    title: 'Submarket',
    key: 'submarket',
    width: 140,
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.submarket?.value,
  },
  {
    title: 'Tower Owner',
    dataIndex: 'site',
    width: 140,
    key: 'towerOwner',
    sorter: true,
    render: (site: SiteBrowse): ReactNode => site?.towerOwner?.value,
  },
  {
    title: 'USID',
    width: 150,
    key: 'USID',
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.USID,
  },
  {
    title: 'Zip',
    key: 'zip',
    dataIndex: 'site',
    width: 100,
    sorter: true,
    render: (site: SiteBrowse): ReactNode =>
      readOnlyInputNumberRender(site?.zip),
  },
];

export const ADDITIONAL_FILTERS: AdditionalFilter[] = [
  {
    key: 'siteWalkNotScheduled',
    name: 'Site Walk Not Scheduled',
  },
  {
    key: 'siteWalkScheduledToday',
    name: 'Site Walk Scheduled Today',
  },
  {
    key: 'completedAuditNotSavedToFile',
    name: 'Completed Audit Not Saved to File',
  },
];

export const SITE_AUDIT_RANGE_PICKERS: Array<keyof SiteAuditBrowseFilter> = [
  'scopingCompleteForecasted',
  'scheduledSiteWalk',
  'siteAuditComplete',
];

export const SITE_AUDIT_DEFAULT_BROWSE_CONFIGURE_FILTER: SiteAuditBrowseFilter =
  {
    jurisdictionId: undefined,
    scheduledSiteWalk: undefined,
    followUpRequired: undefined,
    siteAuditComplete: undefined,
    projectTypeId: undefined,
    scopingCompleteForecasted: undefined,
    stateId: undefined,
    towerOwnerId: undefined,
  };
